import React, { useEffect, useMemo, useState } from "react";
import {
  Archive,
  ChevronLeft,
  ChevronRight,
  Filter,
  Pause,
  Play,
  RefreshCw,
  Sparkles,
  Ticket,
} from "lucide-react";
import { Badge, Btn, Card, Input } from "../../loyalty/components/primitives";
import { STATUS_TONE } from "../../loyalty/components/tokens";
import CouponDetailPanel from "../components/coupon-detail-panel";

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "scheduled", label: "Scheduled" },
  { value: "draft", label: "Draft" },
  { value: "paused", label: "Paused" },
  { value: "expired", label: "Expired" },
  { value: "archived", label: "Archived" },
];

const TYPE_FILTERS = [
  { value: "all", label: "All types" },
  { value: "percent", label: "Percent" },
  { value: "flat", label: "Flat" },
  { value: "bogo", label: "BOGO" },
  { value: "tiered", label: "Tiered" },
];

const fmtDate = (ms) => {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");

const discountSummary = (c) => {
  const t = c.discount_type;
  const d = c.config?.discount || {};
  if (t === "percent") {
    const cap = d.max_cap ? ` (max ₹${fmtNum(d.max_cap)})` : "";
    return `${d.value ?? 0}%${cap}`;
  }
  if (t === "flat") return `₹${fmtNum(d.value ?? 0)} off`;
  if (t === "bogo") return `Buy ${d.buy_qty ?? 1}, get ${d.get_qty ?? 1}`;
  if (t === "tiered") return `${(d.slabs || []).length} slabs`;
  return t || "—";
};

const codesSummary = (c) => {
  // Series row — backend returns series_id (non-null) and counters.
  if (c.series_id) {
    const gen = Number(c.series_generated ?? c.codes_count ?? 0);
    const target = Number(c.series_target ?? gen);
    return `${fmtNum(gen)} / ${fmtNum(target)}`;
  }
  if (c.single_code) return c.single_code;
  if (c.code) return c.code;
  return c.codes_count ? `${fmtNum(c.codes_count)} codes` : "—";
};

const usedSummary = (c) => {
  const used = Number(c.used_count ?? 0);
  const max = c.config?.limits?.max_uses;
  return max ? `${fmtNum(used)} / ${fmtNum(max)}` : fmtNum(used);
};

const PAGE_SIZES = [10, 25, 50];

const ListTab = ({
  coupons = [],
  loading,
  readOnly = false,
  onCreate,
  onEdit,
  onRefresh,
  onStatusChange,
}) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // Coupon selected for the side detail panel. Null when the panel is closed.
  // Clicking a row opens the panel; the panel exposes an Edit button which
  // calls onEdit (the parent's edit handler) and closes itself.
  const [detailRow, setDetailRow] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coupons.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      if (typeFilter !== "all" && c.discount_type !== typeFilter) return false;
      if (q && !(c.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [coupons, statusFilter, typeFilter, search]);

  // Reset to page 1 when the filters change; clamp when the list shrinks.
  useEffect(() => {
    setPage(0);
  }, [statusFilter, typeFilter, search, pageSize]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const paged = useMemo(
    () => filtered.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [filtered, safePage, pageSize]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Coupons</h2>
          <p className="text-[13px] text-[#64748B] mt-1">
            All single coupons and code series. Click a row to edit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={onRefresh}>
            <RefreshCw size={14} />
            Refresh
          </Btn>
          <Btn variant="primary" onClick={onCreate}>
            <Sparkles size={15} />
            New coupon
          </Btn>
        </div>
      </div>

      <Card className="p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#64748B]">
            <Filter size={14} />
            Status
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.value}
                onClick={() => setStatusFilter(s.value)}
                className={`px-2.5 h-7 text-[12px] font-semibold rounded-full transition-colors ${
                  statusFilter === s.value
                    ? "bg-primary text-white"
                    : "bg-[#F1F4F8] text-[#475569] hover:bg-[#E6E9EF]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="h-5 w-px bg-[#E6E9EF] mx-1" />
          <div className="flex items-center gap-1 flex-wrap">
            {TYPE_FILTERS.map((s) => (
              <button
                key={s.value}
                onClick={() => setTypeFilter(s.value)}
                className={`px-2.5 h-7 text-[12px] font-semibold rounded-full transition-colors ${
                  typeFilter === s.value
                    ? "bg-primary text-white"
                    : "bg-[#F1F4F8] text-[#475569] hover:bg-[#E6E9EF]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="ml-auto" style={{ width: 240 }}>
            <Input
              placeholder="Search by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F8FAFC] text-[#64748B]">
              <tr>
                <th className="text-left font-semibold px-4 py-2.5">Name</th>
                <th className="text-left font-semibold px-4 py-2.5">Status</th>
                <th className="text-left font-semibold px-4 py-2.5">Discount</th>
                <th className="text-left font-semibold px-4 py-2.5">Codes</th>
                <th className="text-left font-semibold px-4 py-2.5">Used</th>
                <th className="text-left font-semibold px-4 py-2.5">Validity</th>
                <th className="text-right font-semibold px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-[#F1F4F8] flex items-center justify-center text-[#94A3B8]">
                        <Ticket size={18} />
                      </div>
                      <div className="text-[13px] font-semibold text-[#334155]">
                        No coupons match
                      </div>
                      <div className="text-[12px] text-[#64748B]">
                        Adjust filters or create a new coupon.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                paged.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setDetailRow(c)}
                    className="border-t border-[#EEF1F5] hover:bg-[#F8FAFC] cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0F172A]">{c.name}</div>
                      {c.description && (
                        <div className="text-[11.5px] text-[#94A3B8] mt-0.5 truncate max-w-[320px]">
                          {c.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_TONE[c.status] || "slate"}>
                        {c.status || "draft"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-[#0F172A]">{discountSummary(c)}</td>
                    <td className="px-4 py-3 text-[#0F172A] tabular-nums font-mono text-[12px]">
                      {codesSummary(c)}
                    </td>
                    <td className="px-4 py-3 text-[#0F172A] tabular-nums">{usedSummary(c)}</td>
                    <td className="px-4 py-3 text-[#475569] tabular-nums">
                      {fmtDate(c.valid_from)} – {fmtDate(c.valid_to)}
                    </td>
                    <td
                      className="px-4 py-3"
                      // Keep lifecycle clicks from opening the detail panel.
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1.5">
                        {c.status === "active" && (
                          <Btn
                            size="sm"
                            variant="ghost"
                            disabled={readOnly}
                            onClick={() => onStatusChange?.(c.id, "paused")}
                          >
                            <Pause size={13} />
                            Pause
                          </Btn>
                        )}
                        {c.status === "paused" && (
                          <Btn
                            size="sm"
                            variant="ghost"
                            disabled={readOnly}
                            onClick={() => onStatusChange?.(c.id, "active")}
                          >
                            <Play size={13} />
                            Resume
                          </Btn>
                        )}
                        {c.status !== "archived" && (
                          <Btn
                            size="sm"
                            variant="ghost"
                            disabled={readOnly}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Archive this coupon? Its codes will stop validating at the till. This is the delete action — archived coupons are kept for audit."
                                )
                              ) {
                                onStatusChange?.(c.id, "archived");
                              }
                            }}
                          >
                            <Archive size={13} />
                            Archive
                          </Btn>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pager */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-[#EEF1F5] text-[12.5px] text-[#64748B]">
            <div className="flex items-center gap-2">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-[#E6E9EF] rounded-md px-1.5 py-1 text-[12.5px] bg-white"
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span>
                {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filtered.length)} of {filtered.length}
              </span>
              <Btn
                size="sm"
                variant="ghost"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft size={14} />
              </Btn>
              <Btn
                size="sm"
                variant="ghost"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                <ChevronRight size={14} />
              </Btn>
            </div>
          </div>
        )}
      </Card>

      {detailRow && (
        <CouponDetailPanel
          coupon={detailRow}
          onClose={() => setDetailRow(null)}
          onEdit={(c) => {
            setDetailRow(null);
            onEdit?.(c);
          }}
        />
      )}
    </div>
  );
};

export default ListTab;
